/**
 * The account wall (R21, ADR-036).
 *
 * It stands between the exposure score and the pathways — deliberately later
 * than the obvious place. Gating the diagnosis protects a signup at the cost of
 * the one screen people actually share, and a wall on arrival converts a small
 * slice of visitors. A wall here converts a large slice of people who have just
 * seen their own number, which is a bigger figure and a better list.
 *
 * It also lands at the right moment: the exposure screen ends with the finding
 * that the neighbourhood is no safer than where they already are. Wanting to
 * know what to do about that is the highest-intent second in the journey.
 *
 * The newsletter is a SEPARATE, unticked box. Bundling it into account creation
 * is unlawful under GDPR — consent must be freely given and unbundled from
 * service provision — and it builds a list that does not open. It converts
 * better as an offer with a reason drawn from the person's own result than as a
 * tax on the thing they came for.
 */
import { useState } from "react";
import { signIn, signUp, signInWithGoogle } from "../lib/auth";
import { saveProfile, NEWSLETTER_CONSENT_TEXT } from "../lib/profile";
import { recordSignal, EVENTS } from "../lib/signals";
import { exposureLabel } from "../utils/exposure";
import { PANEL, NAVY, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN } from "../utils/constants";

/** What sits behind the wall, named concretely. Vague promises convert badly. */
const BEHIND = [
  "Where your skills can take you, ranked by how much exposure each move removes",
  "The roles the government data hasn't caught up with yet",
  "What new work each destination needs — and what you'd already bring",
  "Free-first routes to close the gap, and somewhere to apply it within days",
];

export default function PathwayWall({ scored, occupationTitle, onDone }) {
  const [mode, setMode] = useState("up");          // "up" = create, "in" = sign in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [optIn, setOptIn] = useState(false);       // unticked. Deliberately.
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const finish = async (user) => {
    // Consent is recorded even when it is a "no", so a later change is a change
    // to something, not a first entry that looks like it came from nowhere.
    await saveProfile(user.uid, { newsletterOptIn: optIn });
    // Counted separately from the account, because the whole point of keeping
    // the checkbox unbundled is being able to see the real opt-in rate.
    recordSignal(EVENTS.ACCOUNT_CREATED);
    if (optIn) recordSignal(EVENTS.NEWSLETTER_OPT_IN);
    onDone?.(user);
  };

  const run = async (fn) => {
    setBusy(true); setError(null);
    const r = await fn();
    setBusy(false);
    if (r?.error) { setError(r.error); return; }
    if (r?.user) finish(r.user);
  };

  const score = scored?.exposure;
  const automated = scored?.counts?.automated ?? 0;
  const warnings = scored?.premiumWarnings ?? 0;

  const field = {
    width: "100%", background: NAVY, border: `1px solid ${CREAM30}`, borderRadius: 8,
    padding: "11px 13px", color: CREAM, fontSize: 14, outline: "none",
    fontFamily: "'Space Grotesk', sans-serif",
  };

  return (
    <div style={{ animation: "fadeUp 0.4s ease", display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 26 }}>

      {/* ── What they just learned, and what comes next ────────────────── */}
      <div>
        <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 6px",
                     fontFamily: "'Space Grotesk', sans-serif" }}>
          You've seen the diagnosis. This is the part you act on.
        </h2>

        {typeof score === "number" && (
          <p style={{ color: CREAM60, fontSize: 14, lineHeight: 1.65, margin: "0 0 16px" }}>
            {occupationTitle ? <>Your work as <strong style={{ color: CREAM }}>{occupationTitle}</strong> scores </>
                             : <>Your work scores </>}
            <strong style={{ color: score >= 7 ? AMBER : score >= 4 ? AMBER : GREEN }}>
              {score} — {exposureLabel(score)}
            </strong>
            {automated > 0 && <> across {automated} task{automated === 1 ? "" : "s"} already automatable</>}
            {warnings > 0 && <>, and {warnings === 1 ? "one task is" : `${warnings} tasks are`} work
              the price has already left</>}.
          </p>
        )}

        <div style={{ fontSize: 11, color: TEAL, letterSpacing: 1, textTransform: "uppercase",
                      fontFamily: "'JetBrains Mono', monospace", marginBottom: 10 }}>
          Behind this
        </div>
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex",
                     flexDirection: "column", gap: 9 }}>
          {BEHIND.map((b) => (
            <li key={b} style={{ display: "flex", gap: 10, alignItems: "flex-start",
                                 color: CREAM60, fontSize: 13.5, lineHeight: 1.55 }}>
              <span style={{ color: TEAL, fontFamily: "'JetBrains Mono', monospace",
                             fontSize: 12, marginTop: 1 }}>→</span>
              {b}
            </li>
          ))}
        </ul>

        <p style={{ color: CREAM30, fontSize: 12, lineHeight: 1.6, margin: "18px 0 0" }}>
          An account also keeps this assessment dated, so when you come back you see
          what moved rather than starting again. Your résumé is not stored — only the
          shape of the work, never your name or employer.
        </p>
      </div>

      {/* ── The form ───────────────────────────────────────────────────── */}
      <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 22 }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {[["up", "Create account"], ["in", "Sign in"]].map(([m, label]) => (
            <button key={m} onClick={() => { setMode(m); setError(null); }}
              style={{ flex: 1, background: mode === m ? TEAL : "transparent",
                       color: mode === m ? NAVY : CREAM60,
                       border: `1px solid ${mode === m ? TEAL : CREAM30}`,
                       borderRadius: 7, padding: "8px 0", fontSize: 13,
                       fontWeight: mode === m ? 700 : 500, cursor: "pointer" }}>
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div role="alert" style={{ background: `${AMBER}12`, border: `1px solid ${AMBER}40`,
                                     borderRadius: 8, padding: "9px 12px", marginBottom: 12,
                                     fontSize: 12.5, color: AMBER, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <button onClick={() => run(signInWithGoogle)} disabled={busy}
          style={{ width: "100%", background: CREAM, color: NAVY, border: "none", borderRadius: 8,
                   padding: "11px 0", fontSize: 14, fontWeight: 600,
                   cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, marginBottom: 14 }}>
          Continue with Google
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ flex: 1, height: 1, background: BORDER }} />
          <span style={{ fontSize: 11, color: CREAM30,
                         fontFamily: "'JetBrains Mono', monospace" }}>or</span>
          <span style={{ flex: 1, height: 1, background: BORDER }} />
        </div>

        <form onSubmit={(e) => {
          e.preventDefault();
          run(() => (mode === "up" ? signUp(email, password) : signIn(email, password)));
        }}>
          <label htmlFor="pw-email" style={{ display: "block", fontSize: 11, color: CREAM60,
                                             marginBottom: 5, fontFamily: "'JetBrains Mono', monospace" }}>
            EMAIL
          </label>
          <input id="pw-email" type="email" required value={email} autoComplete="email"
                 onChange={(e) => setEmail(e.target.value)} style={{ ...field, marginBottom: 12 }} />

          <label htmlFor="pw-pass" style={{ display: "block", fontSize: 11, color: CREAM60,
                                            marginBottom: 5, fontFamily: "'JetBrains Mono', monospace" }}>
            PASSWORD
          </label>
          <input id="pw-pass" type="password" required minLength={6} value={password}
                 autoComplete={mode === "up" ? "new-password" : "current-password"}
                 onChange={(e) => setPassword(e.target.value)} style={{ ...field, marginBottom: 16 }} />

          {/* Separate. Unticked. Its own reason. See the header note. */}
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
                          background: `${TEAL}08`, border: `1px solid ${TEAL}30`, borderRadius: 8,
                          padding: "12px 13px", marginBottom: 16 }}>
            <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)}
                   style={{ marginTop: 2, accentColor: TEAL, width: 15, height: 15, flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: CREAM60, lineHeight: 1.55 }}>
              <strong style={{ color: CREAM, fontWeight: 600 }}>{NEWSLETTER_CONSENT_TEXT}</strong>
              <br />
              The thinking behind the four axes you just saw — where the premium is going,
              and what people are doing about it. Optional, and you keep full access either way.
            </span>
          </label>

          <button type="submit" disabled={busy}
            style={{ width: "100%", background: TEAL, color: NAVY, border: "none", borderRadius: 8,
                     padding: "12px 0", fontSize: 14, fontWeight: 700,
                     cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {busy ? "One moment…" : mode === "up" ? "Create account & continue" : "Sign in & continue"}
          </button>
        </form>

        <p style={{ color: CREAM30, fontSize: 11, lineHeight: 1.6, margin: "14px 0 0" }}>
          You can export or delete everything at any time. Nobody at SignalPath can see
          an individual result — reporting is aggregate only, with a minimum group size of five.
        </p>
      </div>
    </div>
  );
}
